import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * Badge — formato institucional GOVIO.
 *
 * Não é "pill" 999px (informal demais). É retângulo 4px rounded,
 * uppercase, tracking-wide — idioma de papel timbrado / TCE.
 */
const badgeVariants = cva(
  [
    "inline-flex items-center whitespace-nowrap",
    "rounded-[4px] border",
    "px-2 py-[2px]",
    "text-[11px] font-semibold uppercase tracking-wide leading-tight",
    "transition-colors",
    "focus:outline-none focus:ring-2 focus:ring-[var(--color-ratia-blue-500)] focus:ring-offset-1",
  ].join(" "),
  {
    variants: {
      variant: {
        // Default = neutro institucional (sem cor primária dominante)
        default: [
          "border-transparent bg-[var(--color-ratia-blue-100)]",
          "text-[var(--color-ratia-blue-900)]",
        ].join(" "),
        secondary: [
          "border-transparent bg-[var(--color-ratia-paper-2)]",
          "text-[var(--color-ratia-ink-2)]",
        ].join(" "),
        destructive:
          "border-transparent bg-[var(--color-ratia-status-danger-bg)] text-[var(--color-ratia-status-danger-fg)]",
        outline:
          "border-[var(--color-ratia-line)] bg-card text-[var(--color-ratia-ink-2)]",

        // ── Status semânticos (Lei 14.133) ─────────────────────
        "status-em-andamento":
          "border-transparent bg-[var(--color-ratia-blue-100)] text-[var(--color-ratia-blue-900)]",
        "status-concluido":
          "border-transparent bg-[var(--color-ratia-status-success-bg)] text-[var(--color-ratia-status-success-fg)]",
        "status-cancelado":
          "border-transparent bg-[var(--color-ratia-status-danger-bg)] text-[var(--color-ratia-status-danger-fg)]",
        "status-aguardando":
          "border-transparent bg-[var(--color-ratia-status-warning-bg)] text-[var(--color-ratia-status-warning-fg)]",
        "status-pulado":
          "border-transparent bg-[var(--color-ratia-paper-2)] text-[var(--color-ratia-ink-3)]",

        // ── Setores (Compras, Financeiro, Jurídico, etc.) ──────
        "sector-compras":
          "border-transparent bg-[var(--color-ratia-sector-compras-bg)] text-[var(--color-ratia-sector-compras-fg)]",
        "sector-financeiro":
          "border-transparent bg-[var(--color-ratia-sector-financeiro-bg)] text-[var(--color-ratia-sector-financeiro-fg)]",
        "sector-juridico":
          "border-transparent bg-[var(--color-ratia-sector-juridico-bg)] text-[var(--color-ratia-sector-juridico-fg)]",
        "sector-presidencia":
          "border-transparent bg-[var(--color-ratia-sector-presidencia-bg)] text-[var(--color-ratia-sector-presidencia-fg)]",
        "sector-externo":
          "border-transparent bg-[var(--color-ratia-sector-externo-bg)] text-[var(--color-ratia-sector-externo-fg)]",
        "sector-fornecedor":
          "border-transparent bg-[var(--color-ratia-sector-fornecedor-bg)] text-[var(--color-ratia-sector-fornecedor-fg)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
